/**
 * 파괴 시스템 (FR-004, research §2).
 * 충돌 시작 쌍의 pre-impact 속도(스텝 직전 스냅샷)로 상대 선속도 크기를 계산해
 * 오브젝트별 임계값과 비교. 스텝 이후의 linvel()은 충돌이 이미 해소돼 반동/감쇠로
 * 속도가 줄어 있으므로 판정에 쓰지 않는다(책상 정면 충돌도 임계값을 넘게 됨).
 * 초과 시 원본을 비활성화하고 파편을 스폰한 뒤 object:broken을 발행(instanceId당 1회).
 * 임계값 미달 충돌은 object:impact(인스턴스별 쿨다운), 파편 클래터는
 * debris:impact(전역 스로틀)로 발행한다. 물리 스텝이 반환된 이후 호출되므로
 * 이 시점에는 바디 변형이 안전하다.
 */
import * as THREE from 'three';
import type { EventBus } from '../core/EventBus';
import type { Clock } from '../core/Clock';
import type { Vec3 } from '../core/types';
import type { ContactPair } from '../physics/PhysicsWorld';
import type { ObjectSpawner, SpawnedObject } from './ObjectSpawner';
import type { DebrisPool } from './DebrisPool';
import { shouldBreak, swingWouldSmash } from './impact';
import { LAYOUT } from '../config/layout';
import { BALANCE } from '../config/balance';
import { boundingBoxOf } from '../physics/fracture';

/** 비파괴 object:impact 발행 최소 상대속도 (m/s). */
const IMPACT_MIN_SPEED = 1.5;
/** object:impact 인스턴스별 쿨다운 (초, 게임 시간) — 접촉 스팸 방지. */
const IMPACT_COOLDOWN = 0.15;
/** debris:impact 발행 최소 상대속도 (m/s). */
const DEBRIS_IMPACT_MIN_SPEED = 2.5;
/** debris:impact 전역 스로틀 (초, 게임 시간). */
const DEBRIS_IMPACT_THROTTLE = 0.09;
/** 천장 충돌로 즉시 파괴되는 최소 상대속도 (m/s) — 살짝 스치는 접촉은 튕겨나가게 둔다. */
const CEILING_BREAK_MIN_SPEED = 2.0;
/** 바닥 윗면 y — 책상 밖(앞/옆)에서 잡은 물건을 내리치는 표면(InputController 클램프와 동기). */
const FLOOR_TOP_Y = LAYOUT.floor.y + LAYOUT.floor.size[1] / 2;

export class DestructionSystem {
  private vA = new THREE.Vector3();
  private vB = new THREE.Vector3();
  /** instanceId → 마지막 object:impact 발행 시각(게임 시간 초). */
  private lastImpactAt = new Map<number, number>();
  /** 마지막 debris:impact 발행 시각(게임 시간 초). */
  private lastDebrisImpactAt = -Infinity;
  /**
   * grabbed instanceId → 직전 스텝 위치 + 이번 스텝 스윙 속도/방향.
   * 잡은 물건은 kinematic이라 body.linvel()이 0으로 스냅샷돼 상대속도 판정이 무력화된다
   * (checkGrabbedDeskSmash가 기하를 쓰는 이유와 동일). 위치 델타로 스윙 속도를 직접 구해
   * maybeGrabbedSmash의 파괴 판정·임팩트 방향에 쓴다.
   */
  private grabbedMotion = new Map<
    number,
    { prev: THREE.Vector3; lastMs: number; speed: number; down: number; prevDown: number; dir: THREE.Vector3 }
  >();
  /** 책상 히트 수(중량 물건을 세게 내리쳐 부술 때마다 +1). hits회째에 책상 반파. */
  private deskHits = 0;
  private deskDestroyed = false;

  constructor(
    private spawner: ObjectSpawner,
    private debris: DebrisPool,
    private bus: EventBus,
    private clock: Clock,
  ) {
    const reset = (): void => {
      this.deskHits = 0;
      this.deskDestroyed = false;
      // instanceId는 단조 증가(재사용 없음)라 지난 세션 엔트리는 영구 잔류 — 리셋 때 비운다.
      this.lastImpactAt.clear();
    };
    this.bus.on('session:start', reset);
    this.bus.on('session:reset', reset);
    // 외부 발행(레이지 무브 책상 뒤집기)에도 동기화 — 이후 잡기 스매시 표면 판정이 바닥이 된다.
    this.bus.on('desk:destroyed', () => {
      this.deskDestroyed = true;
    });
  }

  /** 임계값 무시하고 지정 오브젝트를 즉시 파괴(?debug shatterAll 등). */
  forceBreak(instanceId: number, dir: Vec3, speed: number): void {
    const obj = this.spawner.objects.get(instanceId);
    if (!obj || obj.state === 'broken') return;
    this.breakObject(instanceId, obj, new THREE.Vector3(dir.x, dir.y, dir.z), speed, this.clock.now);
  }

  /** 공통 파괴 처리: 마크 + 파편 버스트 + object:broken 발행. dir은 정규화 전(0이면 위로 폴백). */
  private breakObject(
    instanceId: number,
    obj: SpawnedObject,
    dir: THREE.Vector3,
    speed: number,
    now: number,
  ): void {
    const t = obj.body.translation();
    const r = obj.body.rotation();
    const worldPos = new THREE.Vector3(t.x, t.y, t.z);
    const worldQuat = new THREE.Quaternion(r.x, r.y, r.z, r.w);
    const impactDir = dir.lengthSq() > 1e-6 ? dir.clone().normalize() : new THREE.Vector3(0, 1, 0);

    // 파괴된 오브젝트의 임팩트 쿨다운 엔트리 정리 — forceBreak 경로 포함 공통 지점.
    this.lastImpactAt.delete(instanceId);
    this.spawner.markBroken(instanceId);
    this.debris.spawnBurst(obj.def, worldPos, worldQuat, impactDir, now, obj.variant, speed);
    this.bus.emit('object:broken', {
      instanceId,
      defId: obj.def.id,
      position: { x: worldPos.x, y: worldPos.y, z: worldPos.z },
      impactDir: { x: impactDir.x, y: impactDir.y, z: impactDir.z },
      impactSpeed: speed,
    });
  }

  /**
   * 매 고정 스텝(물리 스텝 직전) 호출. 잡은(grabbed) 물건에 대해:
   *  1) 스윙 속도/방향을 위치 델타로 추적(kinematic이라 linvel 신뢰 불가) → maybeGrabbedSmash용.
   *  2) 책상 속으로 밀어넣으면(관통) 즉시 파괴 — kinematic은 상판을 뚫고 지나가 충돌 이벤트만으론
   *     슬라이드-관통을 못 잡으므로 기하로 직접 검사(책상 XZ 안 + 밑면이 상판 아래로 파고듦).
   */
  checkGrabbedDeskSmash(): void {
    const halfW = LAYOUT.desk.size[0] / 2;
    const halfD = LAYOUT.desk.size[2] / 2;
    const stillGrabbed = new Set<number>();
    for (const obj of this.spawner.objects.values()) {
      if (obj.state !== 'grabbed') continue;
      stillGrabbed.add(obj.instanceId);
      const t = obj.body.translation();
      const cur = new THREE.Vector3(t.x, t.y, t.z);

      // 스윙 속도·방향 추적(위치 델타 / **실경과 시간**). 갓 잡은 프레임은 이전 위치가 없어 0.
      // - 실시간(performance.now) 기준: 포인터는 실시간으로 움직이므로 게임시간 fixedDt로 나누면
      //   슬로모(timeScale 0.25)·저fps에서 속도가 4~6배 부풀려져 데미지가 왜곡된다.
      // - downSpeed는 max(이번, 직전 스텝의 **하향 성분**): 상판 클램프로 도착 스텝 델타가 잘려도
      //   직전 스텝의 하향 속도로 보정하되, 수평 스와이프의 '크기'가 현재 하향 '방향'과 섞여
      //   풀스피드 내리치기로 오판되지 않도록 성분별로 기억한다.
      let speed = 0;
      let downSpeed = 0;
      let dir = new THREE.Vector3(0, -1, 0);
      const nowMs = performance.now();
      const m = this.grabbedMotion.get(obj.instanceId);
      if (m) {
        const delta = cur.clone().sub(m.prev);
        const realDt = Math.max(0.008, (nowMs - m.lastMs) / 1000); // 같은 프레임 다중 스텝 가드
        m.prevDown = m.down;
        m.speed = delta.length() / realDt;
        // 주의: normalize()는 delta를 in-place 변형 — 하향 성분은 반드시 그 전에 계산할 것.
        m.down = delta.y < 0 ? -delta.y / realDt : 0; // 이번 스텝 하향 성분 속도
        if (delta.lengthSq() > 1e-8) m.dir.copy(delta.normalize());
        m.prev.copy(cur);
        m.lastMs = nowMs;
        speed = m.speed;
        downSpeed = Math.max(m.down, m.prevDown);
        dir = m.dir;
      } else {
        this.grabbedMotion.set(obj.instanceId, { prev: cur, lastMs: nowMs, speed: 0, down: 0, prevDown: 0, dir: dir.clone() });
      }

      const gbb = boundingBoxOf(obj.def.primitive);
      const overDesk = Math.abs(t.x) <= halfW && Math.abs(t.z) <= halfD;
      const bottom = t.y - gbb[1] / 2;
      // 표면에 세게 "내리쳐야" 파괴: 표면에 닿고 하향 스윙 속도가 임계값을 넘을 때만.
      // 표면 = 책상 XZ 안(반파 전)이면 상판, 그 외(책상 앞/옆 — 던졌다 안 깨진 물건을 다시
      // 집는 곳)는 바닥. InputController가 같은 면으로 클램프해 살살 내리면 얹히기만 하고,
      // 예전처럼 표면 아래로 파고들자마자 즉시 파괴되는 일은 없다.
      const onDesk = !this.deskDestroyed && overDesk;
      const surfaceY = onDesk ? LAYOUT.desk.topY : FLOOR_TOP_Y;
      if (bottom <= surfaceY + 0.03 && downSpeed >= obj.def.breakThreshold) {
        this.forceBreak(obj.instanceId, { x: 0, y: -1, z: 0 }, downSpeed);
        // 책상 타격은 상판에서 중량 물건(모니터/맥북)을 부순 경우만 1히트(바닥 스매시 제외).
        if (onDesk && obj.def.mass >= BALANCE.deskBreak.minMass) {
          this.registerDeskHit({ x: t.x, y: LAYOUT.desk.topY, z: t.z }, downSpeed);
        }
        continue;
      }
      // 다른 물건으로 세게 휘두름 → 둘 다 파괴(기하 기반, 충돌 이벤트에 의존하지 않음).
      this.trySwingSmash(obj, cur, gbb, speed, dir);
    }
    // 더 이상 잡고 있지 않은(놓음/던짐/파괴) 항목 정리 — 재-그랩 시 속도 스파이크 방지.
    for (const id of this.grabbedMotion.keys()) if (!stillGrabbed.has(id)) this.grabbedMotion.delete(id);
  }

  /**
   * 책상 히트 등록. 중량 물건 3개(모니터×2+맥북)를 전부 세게 내리치면(hits회째)
   * desk:destroyed 발행(상판 반파 — DeskBreak/오디오/데칼이 반응) 후 남은 물건 전부를
   * 한꺼번에 파괴한다(피날레).
   */
  private registerDeskHit(at: Vec3, impactSpeed: number): void {
    if (this.deskDestroyed) return;
    this.deskHits += 1;
    if (this.deskHits < BALANCE.deskBreak.hits) return;
    this.deskDestroyed = true;
    this.bus.emit('desk:destroyed', { position: at, impactSpeed });
    // 남은 물건 일괄 파괴 — 위로 튀어오르며 함께 터진다(책상이 무너지므로 파편은 바닥으로).
    for (const obj of [...this.spawner.objects.values()]) {
      if (obj.state === 'broken') continue;
      this.forceBreak(obj.instanceId, { x: 0, y: 1, z: 0 }, 12);
    }
  }

  /** 물리 스텝 후 충돌 쌍 처리. */
  process(pairs: ContactPair[], now: number): void {
    for (const pair of pairs) {
      // 이번 배치에서 이미 부서진 오브젝트의 body는 제거됐으므로, 그 body를 참조하는 쌍은
      // 건너뛴다(제거된 body 접근 시 Rapier WASM 트랩 방지).
      if (this.isRemoved(pair.a.instanceId) || this.isRemoved(pair.b.instanceId)) continue;

      // 스텝 직전 스냅샷된 pre-impact 속도로 상대속도 계산 (헤더 doc comment 참고).
      this.vA.set(pair.preVelA.x, pair.preVelA.y, pair.preVelA.z);
      this.vB.set(pair.preVelB.x, pair.preVelB.y, pair.preVelB.z);
      const relSpeed = this.vA.distanceTo(this.vB);

      // 천장 충돌: 위로 던져 천장에 부딪힌 오브젝트는 임계값과 무관하게 즉시 파괴(레이지 손맛).
      if (this.maybeCeilingBreak(pair, relSpeed)) continue;

      this.consider(pair.a.instanceId, pair.bodyA, this.vA, relSpeed, now);
      this.consider(pair.b.instanceId, pair.bodyB, this.vB, relSpeed, now);

      this.maybeDebrisImpact(pair, relSpeed, now);
    }
  }

  /** 오브젝트↔천장 충돌 시 즉시 파괴. 처리했으면 true(해당 쌍의 다른 판정은 건너뜀). */
  private maybeCeilingBreak(pair: ContactPair, relSpeed: number): boolean {
    const aCeil = pair.a.kind === 'ceiling';
    const bCeil = pair.b.kind === 'ceiling';
    if (!aCeil && !bCeil) return false;
    const objMeta = aCeil ? pair.b : pair.a;
    const objVel = aCeil ? this.vB : this.vA;
    if (objMeta.kind !== 'object' || objMeta.instanceId === undefined) return false;
    if (relSpeed < CEILING_BREAK_MIN_SPEED) return false; // 살짝 스침은 튕겨나가게.
    const obj = this.spawner.objects.get(objMeta.instanceId);
    if (!obj || obj.state === 'broken') return false;
    // breakObject가 동일한 <1e-6 → (0,1,0) 상향 폴백을 재적용하므로 원속도를 그대로 넘긴다.
    this.forceBreak(objMeta.instanceId, { x: objVel.x, y: objVel.y, z: objVel.z }, relSpeed);
    return true;
  }

  /**
   * 잡은 물건이 다른 온전한 물건과 겹치고, 스윙 속도가 상대 임계값을 넘으면 둘 다 파괴한다.
   * 잡은 물건은 kinematic이라 충돌 이벤트(START 1회)·linvel 판정이 무력하므로 — 데스크 스매시와
   * 동일하게 — 매 스텝 기하(AABB 겹침) + 위치 델타 스윙 속도로 직접 검사한다.
   */
  private trySwingSmash(
    held: SpawnedObject,
    pos: THREE.Vector3,
    heldBB: [number, number, number],
    speed: number,
    dir: THREE.Vector3,
  ): void {
    for (const other of this.spawner.objects.values()) {
      if (other === held || other.state !== 'intact') continue;
      const ot = other.body.translation();
      const obb = boundingBoxOf(other.def.primitive);
      if (swingWouldSmash(pos, heldBB, ot, obb, speed, other.def.breakThreshold)) {
        const d: Vec3 = { x: dir.x, y: dir.y, z: dir.z };
        this.forceBreak(other.instanceId, d, speed);
        this.forceBreak(held.instanceId, d, speed);
        return; // 잡은 물건도 파괴됐으니 종료.
      }
    }
  }

  /** 파편 클래터: debris↔debris 또는 debris↔static 충돌의 잔여 충격 이벤트(전역 스로틀). */
  private maybeDebrisImpact(pair: ContactPair, relSpeed: number, now: number): void {
    if (relSpeed < DEBRIS_IMPACT_MIN_SPEED) return;
    if (now - this.lastDebrisImpactAt < DEBRIS_IMPACT_THROTTLE) return;
    const aDebris = pair.a.kind === 'debris';
    const bDebris = pair.b.kind === 'debris';
    const isClatter =
      (aDebris && bDebris) ||
      (aDebris && pair.b.kind === 'static') ||
      (bDebris && pair.a.kind === 'static');
    if (!isClatter) return;
    const body = aDebris ? pair.bodyA : pair.bodyB;
    if (!body.isValid()) return; // 풀에서 이미 회수된 파편 body 방어.
    const t = body.translation();
    this.lastDebrisImpactAt = now;
    this.bus.emit('debris:impact', { relSpeed, position: { x: t.x, y: t.y, z: t.z } });
  }

  /** 해당 오브젝트가 이미 파괴되어 body가 제거됐는지. */
  private isRemoved(instanceId: number | undefined): boolean {
    if (instanceId === undefined) return false;
    return this.spawner.objects.get(instanceId)?.state === 'broken';
  }

  private consider(
    instanceId: number | undefined,
    body: import('@dimforge/rapier3d-compat').RigidBody,
    vel: THREE.Vector3,
    relSpeed: number,
    now: number,
  ): void {
    if (instanceId === undefined) return;
    const obj = this.spawner.objects.get(instanceId);
    if (!obj || obj.state === 'broken' || obj.state === 'grabbed') return;
    if (!shouldBreak(relSpeed, obj.def.breakThreshold)) {
      // 임계값 미달의 비파괴 충격 — 타격음/미세 셰이크용 이벤트 발행.
      this.maybeObjectImpact(instanceId, obj.def.id, body, relSpeed, now);
      return;
    }

    // 파괴 확정(임팩트 쿨다운 엔트리는 breakObject가 정리).
    this.breakObject(instanceId, obj, vel, relSpeed, now);
  }

  /** 임계값 미달의 비파괴 충격 이벤트. 인스턴스별 쿨다운으로 접촉 스팸 방지. */
  private maybeObjectImpact(
    instanceId: number,
    defId: string,
    body: import('@dimforge/rapier3d-compat').RigidBody,
    relSpeed: number,
    now: number,
  ): void {
    if (relSpeed < IMPACT_MIN_SPEED) return;
    const last = this.lastImpactAt.get(instanceId);
    if (last !== undefined && now - last < IMPACT_COOLDOWN) return;
    this.lastImpactAt.set(instanceId, now);
    const t = body.translation();
    this.bus.emit('object:impact', {
      instanceId,
      defId,
      relSpeed,
      position: { x: t.x, y: t.y, z: t.z },
    });
  }
}
